import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

import { Input } from '@/components/form/Input';
import { Button } from '@/components/ui/button';
import type { KV } from '@/sections/asset/components/tabs/credentials/RecipeWizard/recipePayload';

interface KVRowsEditorProps {
  rows: KV[];
  onChange: (rows: KV[]) => void;
  keyHeader?: string;
  valueHeader?: string;
  keyPlaceholder?: string | ((index: number) => string);
  valuePlaceholder?: string;
  addLabel?: string;
  // Minimum number of rows the user can't remove past. 0 = removable to empty,
  // 1 = always show at least one row (used by Login Credentials).
  minRows?: number;
  testIdPrefix: string;
}

// Two-column key/value list with add/remove controls. Used for both the
// inputs (credentials) form and the extra-headers form on the HTTP step.
export function KVRowsEditor({
  rows,
  onChange,
  keyHeader = 'Name',
  valueHeader = 'Value',
  keyPlaceholder = '',
  valuePlaceholder = 'value',
  addLabel = 'Add row',
  minRows = 0,
  testIdPrefix,
}: KVRowsEditorProps) {
  const update = (i: number, field: 'key' | 'value', val: string) =>
    onChange(
      rows.map((row, idx) => (idx === i ? { ...row, [field]: val } : row))
    );
  const add = () => onChange([...rows, { key: '', value: '' }]);
  const remove = (i: number) =>
    onChange(rows.length > minRows ? rows.filter((_, idx) => idx !== i) : rows);

  const placeholderFor = (i: number) =>
    typeof keyPlaceholder === 'function' ? keyPlaceholder(i) : keyPlaceholder;

  return (
    <div className="border-chariot-stroke rounded-md border p-4">
      {rows.length > 0 && (
        <div className="text-chariot-text-secondary mb-2 grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-medium uppercase">
          <span>{keyHeader}</span>
          <span>{valueHeader}</span>
          <span className="w-8"></span>
        </div>
      )}
      {rows.map((row, i) => (
        <div
          key={i}
          className="mb-2 grid grid-cols-[1fr_1fr_auto] items-end gap-2"
        >
          <Input
            name={`${testIdPrefix}-key-${i}`}
            placeholder={placeholderFor(i)}
            value={row.key}
            onChange={e => update(i, 'key', e.target.value)}
            data-testid={`${testIdPrefix}-key-${i}`}
          />
          <Input
            name={`${testIdPrefix}-value-${i}`}
            placeholder={valuePlaceholder}
            value={row.value}
            onChange={e => update(i, 'value', e.target.value)}
            data-testid={`${testIdPrefix}-value-${i}`}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => remove(i)}
            disabled={rows.length <= minRows}
            aria-label="Remove row"
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={add}>
        <PlusIcon className="size-4" />
        {addLabel}
      </Button>
    </div>
  );
}
